// InviteCodePage.js v2.0 - Mobile Optimized
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, Building2, Lock, CheckCircle, Shield, Sun, Moon } from 'lucide-react';
import { INVITE_CODE, NDA_INVITE_CODE, BASTROP_RATE_CODE, LYT_INFO, getOnboardingConfig, getNdaRateConfig } from '../config/constants';
import { getTheme, useMobile } from '../utils/theme';

function InviteCodePage({ setCurrentPage, setOnboardingContext, darkMode, setDarkMode }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [codeVerified, setCodeVerified] = useState(false);
  const [codeType, setCodeType] = useState(null);
  const isMobile = useMobile();

  const theme = getTheme(darkMode);
  const { accentMarketing: accentPrimary, accentMarketingAlt: accentSecondary, accentMarketingTertiary: accentNDA, gradientMarketing: accentGradient, error: accentError, bgAlt: bgColor, cardBg, text: textColor, textMedium: mutedColor, logoLY, logoT, logoComm } = theme.colors;

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    setError('');
    const trimmedCode = code.toLowerCase().trim();
    if (trimmedCode === INVITE_CODE.toLowerCase()) {
      setCodeVerified(true);
      setCodeType('onboarding');
      if (setOnboardingContext) setOnboardingContext(null);
    } else if (trimmedCode === NDA_INVITE_CODE.toLowerCase()) {
      setCodeVerified(true);
      setCodeType('nda');
    } else if (trimmedCode === BASTROP_RATE_CODE) {
      // Redirect to standalone rate card page
      setCurrentPage('bastrop-rates');
      return;
    } else if (getNdaRateConfig(trimmedCode)) {
      // NDA-gated rate card code — pass config and route to combined NDA+rate page
      if (setOnboardingContext) setOnboardingContext(getNdaRateConfig(trimmedCode));
      setCurrentPage('nda-rate-card');
      return;
    } else {
      // Check dynamic project codes
      const projectConfig = getOnboardingConfig(trimmedCode);
      if (projectConfig) {
        if (setOnboardingContext) setOnboardingContext(projectConfig);
        // Route based on onboarding type: contractor companies vs individual workers
        setCurrentPage(projectConfig.onboardingType === 'contractor' ? 'contractor-onboarding' : 'employee-onboarding');
        return;
      }
      setError('Invalid invite code. Please check with your administrator.');
    }
  };

  const handleTypeSelect = (type) => {
    if (type === 'employee') setCurrentPage('employee-onboarding');
    else if (type === 'contractor') setCurrentPage('contractor-onboarding');
    else if (type === 'nda') setCurrentPage('nda-sign');
  };

  const resetCode = () => {
    setCodeVerified(false);
    setCodeType(null);
    setCode('');
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor }}>
      {/* Dark/Light Mode Toggle Bar */}
      <div style={{
        backgroundColor: darkMode ? '#112240' : '#f1f5f9',
        padding: isMobile ? '8px 16px' : '6px 20px',
        display: 'flex',
        justifyContent: 'flex-end'
      }}>
        {setDarkMode && (
          <button
            onClick={() => setDarkMode(!darkMode)}
            aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: darkMode ? 'rgba(255,255,255,0.8)' : '#64748b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.85rem',
              padding: '8px 12px',
              borderRadius: '6px',
              minHeight: '44px',
            }}
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            <span className="hide-mobile">{darkMode ? 'Light' : 'Dark'} Mode</span>
          </button>
        )}
      </div>

      {/* Header */}
      <header style={{
        padding: isMobile ? '12px 16px' : '16px 20px',
        backgroundColor: darkMode ? '#0d1b2a' : '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`
      }}>
        <button
          onClick={() => setCurrentPage('home')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'transparent',
            border: 'none',
            color: darkMode ? 'rgba(255,255,255,0.8)' : '#64748b',
            fontSize: isMobile ? '0.9rem' : '1rem',
            cursor: 'pointer',
            padding: '10px 12px',
            borderRadius: '8px',
            minHeight: '44px',
          }}
        >
          <ArrowLeft size={isMobile ? 18 : 20} /> {!isMobile && 'Back'}
        </button>
        <div style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '700', color: textColor }}>
          <span style={{ color: accentPrimary }}>Team</span> Onboarding
        </div>
        <button
          onClick={() => setCurrentPage('home')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '10px',
            minHeight: '44px',
            minWidth: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '700' }}>
            <span style={{ color: logoLY }}>ly</span><span style={{ color: logoT }}>t</span>
          </div>
        </button>
      </header>

      {/* Main Content */}
      <div style={{
        maxWidth: '400px',
        margin: isMobile ? '24px auto' : '60px auto',
        padding: isMobile ? '0 16px' : '0 20px'
      }}>
        <div style={{ width: '100%' }}>
          {!codeVerified ? (
            <div style={{
              backgroundColor: cardBg,
              borderRadius: isMobile ? '12px' : '16px',
              padding: isMobile ? '24px 20px' : '32px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.1)'
            }}>
              <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '24px' }}>
                <div style={{
                  width: isMobile ? '56px' : '64px',
                  height: isMobile ? '56px' : '64px',
                  borderRadius: '50%',
                  background: accentGradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <Lock size={isMobile ? 24 : 28} color="#fff" />
                </div>
                <h2 style={{ color: textColor, margin: '0 0 8px', fontSize: isMobile ? '1.35rem' : '1.5rem' }}>Welcome to Onboarding</h2>
                <p style={{ color: mutedColor, margin: 0, fontSize: '0.9rem' }}>Enter your invite code to begin</p>
              </div>
              <form onSubmit={handleCodeSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: textColor, fontWeight: '500', fontSize: '0.9rem' }}>Invite Code</label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter your invite code"
                    style={{
                      width: '100%',
                      padding: isMobile ? '16px' : '14px 16px',
                      fontSize: '16px',
                      border: `2px solid ${error ? accentError : (darkMode ? '#374151' : '#e5e7eb')}`,
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      color: '#1f2937',
                      boxSizing: 'border-box',
                      outline: 'none',
                    }}
                    autoComplete="off"
                    autoCapitalize="none"
                  />
                  {error && (
                    <div role="alert" style={{
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#dc2626',
                      padding: '12px',
                      borderRadius: '8px',
                      marginTop: '12px',
                      fontSize: '0.9rem'
                    }}>
                      {error}
                    </div>
                  )}
                </div>
                <button type="submit" style={{
                  width: '100%',
                  padding: isMobile ? '16px' : '14px',
                  background: accentGradient,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minHeight: '50px',
                }}>Continue</button>
              </form>
              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <p style={{ color: mutedColor, fontSize: '0.85rem', margin: 0 }}>
                  Don't have an invite code? Contact <a href={`mailto:${LYT_INFO.email}`} style={{ color: accentPrimary, textDecoration: 'underline' }}>{LYT_INFO.email}</a>
                </p>
              </div>
            </div>
          ) : codeType === 'nda' ? (
            <div style={{
              backgroundColor: cardBg,
              borderRadius: isMobile ? '12px' : '16px',
              padding: isMobile ? '24px 20px' : '32px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.1)'
            }}>
              <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '24px' }}>
                <div style={{
                  width: isMobile ? '56px' : '64px',
                  height: isMobile ? '56px' : '64px',
                  borderRadius: '50%',
                  background: accentGradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <CheckCircle size={isMobile ? 24 : 28} color="#fff" />
                </div>
                <h2 style={{ color: textColor, margin: '0 0 8px', fontSize: isMobile ? '1.35rem' : '1.5rem' }}>Code Verified</h2>
                <p style={{ color: mutedColor, margin: 0, fontSize: '0.9rem' }}>You're here to sign an NDA</p>
              </div>
              <button onClick={() => handleTypeSelect('nda')} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: isMobile ? '16px' : '20px', width: '100%', backgroundColor: darkMode ? '#111827' : '#f8fafc', border: `2px solid ${accentNDA}`, borderRadius: '12px', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: `${accentNDA}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Shield size={24} color={accentNDA} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor, marginBottom: '4px' }}>Non-Disclosure Agreement</h3>
                  <p style={{ color: mutedColor, fontSize: '0.9rem', margin: 0 }}>Review and sign the confidentiality agreement</p>
                </div>
              </button>
              <button onClick={resetCode} style={{ display: 'block', width: '100%', marginTop: '24px', padding: '12px', backgroundColor: 'transparent', border: 'none', color: mutedColor, fontSize: '0.9rem', cursor: 'pointer', minHeight: '44px' }}>← Back to code entry</button>
            </div>
          ) : (
            <div style={{
              backgroundColor: cardBg,
              borderRadius: isMobile ? '12px' : '16px',
              padding: isMobile ? '24px 20px' : '32px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.1)'
            }}>
              <div style={{ textAlign: 'center', marginBottom: isMobile ? '20px' : '24px' }}>
                <div style={{
                  width: isMobile ? '56px' : '64px',
                  height: isMobile ? '56px' : '64px',
                  borderRadius: '50%',
                  background: accentGradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <CheckCircle size={isMobile ? 24 : 28} color="#fff" />
                </div>
                <h2 style={{ color: textColor, margin: '0 0 8px', fontSize: isMobile ? '1.35rem' : '1.5rem' }}>Code Verified</h2>
                <p style={{ color: mutedColor, margin: 0, fontSize: '0.9rem' }}>Select your onboarding type to continue</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <button onClick={() => handleTypeSelect('employee')} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: isMobile ? '16px' : '20px', backgroundColor: darkMode ? '#111827' : '#f8fafc', border: `2px solid ${darkMode ? '#374151' : '#e5e7eb'}`, borderRadius: '12px', cursor: 'pointer', textAlign: 'left', minHeight: '80px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: `${accentPrimary}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Users size={24} color={accentPrimary} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor, marginBottom: '4px' }}>Employee</h3>
                    <p style={{ color: mutedColor, fontSize: isMobile ? '0.85rem' : '0.9rem', margin: 0 }}>I'm joining as a W-2 employee</p>
                  </div>
                </button>
                <button onClick={() => handleTypeSelect('contractor')} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: isMobile ? '16px' : '20px', backgroundColor: darkMode ? '#111827' : '#f8fafc', border: `2px solid ${darkMode ? '#374151' : '#e5e7eb'}`, borderRadius: '12px', cursor: 'pointer', textAlign: 'left', minHeight: '80px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: `${accentSecondary}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Building2 size={24} color={accentSecondary} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: textColor, marginBottom: '4px' }}>Contractor / Subcontractor</h3>
                    <p style={{ color: mutedColor, fontSize: isMobile ? '0.85rem' : '0.9rem', margin: 0 }}>I'm registering my company</p>
                  </div>
                </button>
              </div>
              <button onClick={resetCode} style={{ display: 'block', width: '100%', marginTop: '24px', padding: '12px', backgroundColor: 'transparent', border: 'none', color: mutedColor, fontSize: '0.9rem', cursor: 'pointer', minHeight: '44px' }}>← Back to code entry</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InviteCodePage;
